import { Controller, type UseFormReturn } from 'react-hook-form';
import { Input, Select } from '@fiado/react-kit';
import type { UserFieldDefLocal } from '../../api/types/TenantLocal';

interface Props {
    /** Campos a renderizar — YA filtrados a `enabled === true` por el consumidor (ver useTenantUserFields). */
    fields: UserFieldDefLocal[];
    /**
     * El `form` de react-hook-form del form de usuario que embebe esta sección. Se recibe el objeto
     * completo (no solo `register`) porque los `<Select>` del kit son controlados (`onChange(value)`,
     * no compatibles con `register`) y necesitan `control` vía `<Controller>`. Los `<Input>` de texto
     * sí usan `register`. Tipado a `UseFormReturn` (FieldValues) para no acoplar el namespace al
     * schema del caller; el bloque `customFields.<key>` se direcciona por string.
     */
    form: UseFormReturn;
    /** Prefijo de id para a11y (htmlFor/aria). Default `cf`. */
    idPrefix?: string;
}

/**
 * Sección de campos personalizados del tenant dentro de un form de usuario.
 *
 * Los valores viven bajo el namespace `customFields.<key>` para no chocar con email/displayName/roleId.
 * El consumidor mergea `customFields: buildCustomFieldsSchema(fields)` en su zod y arma los defaults
 * con `pickCustomFieldsDefaults(fields, current)` (ver `userFieldsSchema.ts`).
 *
 * Orden de presentación: ALFABÉTICO por `label` (default del proyecto), salvo que el caso pida otra cosa.
 * Si `fields` está vacío, no renderiza nada (tenants sin catálogo no muestran sección).
 */
export function DynamicUserFields({ fields, form, idPrefix = 'cf' }: Props) {
    if (fields.length === 0) return null;

    const sorted = [...fields].sort((a, b) => a.label.localeCompare(b.label));

    return (
        <>
            {sorted.map((field) => {
                const name = `customFields.${field.key}`;
                const id = `${idPrefix}-${field.key}`;
                // react-hook-form anida los errores: errors.customFields?.[key]?.message
                const fieldErrors = form.formState.errors.customFields as
                    | Record<string, { message?: string } | undefined>
                    | undefined;
                const error = fieldErrors?.[field.key]?.message;

                if (field.type === 'select') {
                    return (
                        <Controller
                            key={field.key}
                            control={form.control}
                            name={name}
                            render={({ field: ctrl }) => (
                                <Select
                                    label={field.label}
                                    required={field.required}
                                    value={(ctrl.value as string | undefined) ?? ''}
                                    onChange={ctrl.onChange}
                                    error={error}
                                    placeholder="Elige una opción"
                                    options={(field.options ?? []).map((opt) => ({
                                        value: opt,
                                        label: opt,
                                    }))}
                                />
                            )}
                        />
                    );
                }

                return (
                    <Input
                        key={field.key}
                        id={id}
                        label={field.label}
                        required={field.required}
                        error={error}
                        {...form.register(name)}
                    />
                );
            })}
        </>
    );
}
